<template>
  <div v-if="show" class="modal-overlay" @click.self="handleClose">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ isEditMode ? 'Edit Review' : 'Write a Review' }}</h2>
        <button @click="handleClose" class="close-button" aria-label="Close">×</button>
      </div>

      <div class="modal-body">

        <form @submit.prevent="handleSubmit" class="review-form">
          <!-- Educational Content Rating -->
          <div class="rating-group">
            <label for="educationalContent">Educational Content</label>
            <StarRating
              v-model="formData.educationalContent"
              :interactive="true"
              :show-value="true"
            />
          </div>

          <!-- Engagement Rating -->
          <div class="rating-group">
            <label for="engagement">Engagement</label>
            <StarRating
              v-model="formData.engagement"
              :interactive="true"
              :show-value="true"
            />
          </div>

          <!-- Overall Value Rating -->
          <div class="rating-group">
            <label for="overallValue">Overall Value</label>
            <StarRating
              v-model="formData.overallValue"
              :interactive="true"
              :show-value="true"
            />
          </div>

          <!-- Review Text -->
          <div class="form-group">
            <label for="reviewText">Your Review <span class="required">*</span></label>
            <textarea
              id="reviewText"
              v-model="formData.reviewText"
              required
              rows="6"
              placeholder="Share your thoughts about this course..."
              class="form-input"
            ></textarea>
            <small class="form-help">Tell others about your experience with this course</small>
          </div>

          <!-- Form Actions -->
          <div class="form-actions">
            <button type="button" @click="handleClose" class="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" :disabled="isSubmitting" class="btn btn-primary">
              {{ isSubmitting ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update Review' : 'Submit Review') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { saveReview, updateReview } from '../../services/reviewService.js'
import { validateReview } from '../../models/Review.js'
import { getCurrentUser } from '../../services/authService.js'
import StarRating from '../shared/StarRating.vue'

export default {
  name: 'AddReviewModal',
  components: {
    StarRating
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    courseId: {
      type: String,
      required: true
    },
    reviewId: {
      type: String,
      default: null
    },
    existingReview: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      formData: {
        educationalContent: 0,
        engagement: 0,
        overallValue: 0,
        reviewText: ''
      },
      isSubmitting: false,
      error: null
    }
  },
  computed: {
    isEditMode() {
      return !!this.reviewId && !!this.existingReview
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.existingReview) {
        // Pre-populate form when editing
        this.formData = {
          educationalContent: this.existingReview.educationalContent || 0,
          engagement: this.existingReview.engagement || 0,
          overallValue: this.existingReview.overallValue || 0,
          reviewText: this.existingReview.reviewText || ''
        }
      } else if (!newVal) {
        this.resetForm()
      }
    },
    existingReview: {
      handler(newVal) {
        if (newVal && this.show) {
          // Pre-populate form when review data changes
          this.formData = {
            educationalContent: newVal.educationalContent || 0,
            engagement: newVal.engagement || 0,
            overallValue: newVal.overallValue || 0,
            reviewText: newVal.reviewText || ''
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    async handleSubmit() {
      this.isSubmitting = true
      this.error = null

      // Validate ratings are set
      if (this.formData.educationalContent === 0 || 
          this.formData.engagement === 0 || 
          this.formData.overallValue === 0) {
        this.error = 'Please provide ratings for all categories.'
        this.isSubmitting = false
        return
      }

      try {
        const currentUser = getCurrentUser()
        if (!currentUser) {
          this.error = 'You must be logged in to submit a review.'
          this.isSubmitting = false
          return
        }

        const reviewData = {
          courseId: this.courseId,
          userId: currentUser.id,
          educationalContent: this.formData.educationalContent,
          engagement: this.formData.engagement,
          overallValue: this.formData.overallValue,
          reviewText: this.formData.reviewText.trim()
        }

        const validation = validateReview(reviewData)
        if (!validation.isValid) {
          this.error = validation.errors.join('. ') + '.'
          this.isSubmitting = false
          return
        }

        if (this.isEditMode) {
          await updateReview(this.reviewId, reviewData)
        } else {
          await saveReview(reviewData)
        }
        this.$emit('review-submitted')
        this.handleClose()
      } catch (error) {
        this.error = error.message || 'Failed to submit review. Please try again.'
        console.error('Error submitting review:', error)
      } finally {
        this.isSubmitting = false
      }
    },
    handleClose() {
      this.resetForm()
      this.$emit('close')
    },
    resetForm() {
      this.formData = {
        educationalContent: 0,
        engagement: 0,
        overallValue: 0,
        reviewText: ''
      }
      this.error = null
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--color-surface-light);
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 2px solid var(--color-border-light);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--color-text-primary);
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all var(--transition-normal);
}

.close-button:hover {
  background-color: var(--color-surface-dark);
  color: var(--color-text-primary);
}

.modal-body {
  padding: 30px;
}

.error-message {
  background-color: var(--color-error-light);
  color: var(--color-error-dark);
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid var(--color-error-main);
  font-size: 0.9rem;
}

.review-form {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.rating-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rating-group label {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 1rem;
}

.required {
  color: var(--color-error-main);
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
  resize: vertical;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

.form-help {
  display: block;
  margin-top: 5px;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 10px;
  padding-top: 20px;
  border-top: 2px solid var(--color-border-light);
}

.btn {
  padding: 12px 30px;
  font-size: 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all var(--transition-normal);
}

.btn-primary {
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
}

.btn-primary:disabled {
  background-color: var(--color-border-main);
  cursor: not-allowed;
}

.btn-secondary {
  background-color: var(--color-surface-dark);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border-main);
}

.btn-secondary:hover {
  background-color: var(--color-border-light);
}

@media (max-width: 768px) {
  .modal-content {
    max-width: 100%;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style>

